// Глобалните клавиши на работен лист (window keydown) — едно копие вместо
// петте, които листовете държаха поотделно. Решетката (`useGridNavigation`) и
// търсачката (`useWorksheetSearch`) обработват своите клавиши ПРЕДИ това
// (React handler-ите на root-а вървят преди window listener-ите) и маркират
// обработеното с preventDefault — тук то се пропуска.
//
// Ред на проверките:
//   1. вече обработен клавиш → нищо
//   2. собствен промпт на листа (чернова) → Esc го затваря, другото — нищо
//   3. отворен antd слой (модал, confirm, падащо меню, календар) → нищо: и
//      Ctrl+Enter, и буквите са негови. Иначе Ctrl+Enter в „Нов продукт"
//      записваше и продукта, и цялата доставка, а буква/↓ върху бутон в модал
//      „изтичаше" към търсачката зад него.
//   4. Ctrl/Cmd+Enter → `onPrimary` (запис)
//   5. Esc стълбица: решетка → търсачката; поле → blur + търсачката; самата
//      търсачка → нейният handler; „отникъде" → preventDefault + `onLeave`
//      (стъпка назад към списъка, не изход от модула — без preventDefault
//      глобалната Esc-навигация на SharedAccountGate изхвърля навън)
//   6. ↓ „отникъде" → първият ред
//   7. машина за писане (само десктоп, без модификатори, извън поле): буквата
//      се записва в САМОТО поле ПРЕДИ фокуса — при бързо писане следващите
//      натискания изпреварват React и първата буква се губеше
//
// Всички параметри се четат през ref — listener-ът се закача веднъж, не на
// всеки рендер (инвентаризацията рендира хиляди редове).
import { useEffect, useRef } from "react";
import type { RefObject } from "react";
import { hasOpenOverlay, isEditableTarget } from "../../../lib/overlay";

const TYPING_KEY = /^[a-zA-Zа-яА-Я0-9 ]$/;

type Params = {
  isDesktop: boolean;
  searchInputRef: RefObject<HTMLInputElement | null>;
  // текущият текст на търсачката и смяната му (машината за писане)
  searchTextRef: RefObject<string>;
  setSearch: (value: string) => void;
  isInGrid?: () => boolean;
  // Esc от решетката/поле → търсачката (с маркиран текст)
  focusSearch: () => void;
  // ↓ „отникъде" — false/липса = няма редове, клавишът не се консумира
  focusFirst?: () => boolean;
  onPrimary?: () => void; // Ctrl/Cmd+Enter
  onLeave: () => void; // Esc „отникъде"
  // Собствен (не-antd) промпт върху листа
  prompt?: { isOpen: () => boolean; close: () => void };
  // Esc в поле извън търсачката води към търсачката (false = остава на полето)
  escFromFieldToSearch?: boolean;
};

export const useWorksheetKeyboard = (params: Params) => {
  const paramsRef = useRef(params);
  paramsRef.current = params;

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const current = paramsRef.current;
      if (event.defaultPrevented) return;

      if (current.prompt?.isOpen()) {
        if (event.key === "Escape") {
          event.preventDefault();
          current.prompt.close();
        }
        return;
      }
      if (hasOpenOverlay()) return;

      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        if (!current.onPrimary) return;
        event.preventDefault();
        current.onPrimary();
        return;
      }

      const target = event.target as HTMLElement | null;
      const searchInput = current.searchInputRef.current;
      const inGrid = current.isInGrid?.() ?? false;

      if (event.key === "Escape") {
        if (inGrid) {
          event.preventDefault();
          current.focusSearch();
          return;
        }
        if (isEditableTarget(target)) {
          // Търсачката си има собствен стъпаловиден Esc
          if (target === searchInput) return;
          if (current.escFromFieldToSearch === false) return;
          event.preventDefault();
          target?.blur();
          searchInput?.focus();
          return;
        }
        event.preventDefault();
        current.onLeave();
        return;
      }

      if (event.key === "ArrowDown") {
        if (isEditableTarget(target) || inGrid || !current.focusFirst) return;
        if (current.focusFirst()) event.preventDefault();
        return;
      }

      if (!current.isDesktop) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(target)) return;
      const isBackspace = event.key === "Backspace";
      if (!isBackspace && !TYPING_KEY.test(event.key)) return;
      event.preventDefault();
      const text = current.searchTextRef.current ?? "";
      const next = isBackspace ? text.slice(0, -1) : text + event.key;
      current.setSearch(next);
      if (searchInput) {
        searchInput.value = next;
        searchInput.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
};
